import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { CgEye, CgPen, CgTrash } from "react-icons/cg";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import CustomModal from "../Commaon/CustomModal";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";

const defaultForm = {
  code: "",
  discountType: "PERCENTAGE",
  discountValue: "",
  usagePerUser: "ONCE",
  isActive: true,
};

const discountTypeLabel = {
  PERCENTAGE: "Flat Discount in Percentage",
  FIXED_AMOUNT: "Flat Discount in Fixed Amount",
};

const usageLabel = {
  ONCE: "Once per user",
  MANY: "Many times per user",
};

const formatDiscount = (coupon) => {
  if (coupon.discountType === "PERCENTAGE") return `${coupon.discountValue}%`;
  return `₹${coupon.discountValue}`;
};

const CouponsList = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [form, setForm] = useState(defaultForm);
  const [modalMode, setModalMode] = useState(null);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const totalPages = Math.ceil(totalRecords / limit) || 1;

  const showSnack = (message, type = "success") => {
    setSnack({ notification: { message, type } });
  };

  const getCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(
        `/admin/coupon?page=${pageNo}&limit=${limit}&search=${encodeURIComponent(query.trim())}`,
        { method: "GET" }
      );
      const data = response.data;
      if (data.s === 200) {
        setCoupons(data.r || []);
        setTotalRecords(data.tr || 0);
      } else {
        setCoupons([]);
        setTotalRecords(0);
      }
    } catch (error) {
      setCoupons([]);
      setTotalRecords(0);
      showSnack("Error fetching coupons", "error");
    } finally {
      setLoading(false);
    }
  }, [pageNo, limit, query]);

  useEffect(() => {
    getCoupons();
  }, [getCoupons]);

  useEffect(() => {
    setPageNo(1);
  }, [query]);

  const filteredCoupons = useMemo(() => coupons, [coupons]);

  const openAdd = () => {
    setForm(defaultForm);
    setSelectedCoupon(null);
    setModalMode("add");
  };

  const openEdit = (coupon) => {
    setSelectedCoupon(coupon);
    setForm({
      code: coupon.code || "",
      discountType: coupon.discountType || "PERCENTAGE",
      discountValue: coupon.discountValue ?? "",
      usagePerUser: coupon.usagePerUser || "ONCE",
      isActive: coupon.isActive ?? true,
    });
    setModalMode("edit");
  };

  const validateForm = () => {
    if (!form.code.trim()) return "Coupon code is required";
    const value = Number(form.discountValue);
    if (!Number.isFinite(value) || value <= 0) return "Discount value must be greater than 0";
    if (form.discountType === "PERCENTAGE" && value > 100) return "Percentage discount cannot be greater than 100";
    return "";
  };

  const saveCoupon = async (event) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      showSnack(error, "error");
      return;
    }

    const isEdit = modalMode === "edit";
    const url = isEdit ? `/admin/coupon/${selectedCoupon._id}` : "/admin/coupon";
    try {
      setLoading(true);
      const response = await AuthInterceptor(url, {
        method: isEdit ? "PUT" : "POST",
        body: JSON.stringify({
          ...form,
          code: form.code.trim().toUpperCase(),
          discountValue: Number(form.discountValue),
        }),
      });
      const data = response.data;
      if ([200, 201].includes(data.s)) {
        showSnack(`Coupon ${isEdit ? "updated" : "added"} successfully`);
        setModalMode(null);
        getCoupons();
      } else {
        throw new Error(data.m || "Error saving coupon");
      }
    } catch (error) {
      showSnack(error.message || "Error saving coupon", "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleCouponStatus = async (coupon) => {
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/coupon/${coupon._id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ isActive: !coupon.isActive }),
      });
      const data = response.data;
      if (data.s === 200) {
        showSnack(data.m || "Coupon status updated");
        getCoupons();
      } else {
        throw new Error(data.m || "Error updating coupon status");
      }
    } catch (error) {
      showSnack(error.message || "Error updating coupon status", "error");
    } finally {
      setLoading(false);
    }
  };

  const deleteCoupon = async () => {
    if (!deleteId) return;
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/coupon/${deleteId}`, {
        method: "DELETE",
      });
      const data = response.data;
      if (data.s === 200) {
        showSnack(data.m || "Coupon deleted successfully");
        getCoupons();
      } else {
        throw new Error(data.m || "Error deleting coupon");
      }
    } catch (error) {
      showSnack(error.message || "Error deleting coupon", "error");
    } finally {
      setLoading(false);
      setDeleteId(null);
    }
  };

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && <Toaster notification={snack.notification} onClose={() => setSnack(null)} />}

      <div className="card shadow-sm border-0 rounded-3 p-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <h2 className="mb-2 mb-md-0">Coupons</h2>
          <div className="d-flex gap-2">
            <select
              className="form-select form-select-sm w-auto"
              value={limit}
              onChange={(e) => {
                setPageNo(1);
                setLimit(Number(e.target.value));
              }}
            >
              {[5, 10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
            <button className="btn btn-outline-custom btn-sm" onClick={openAdd}>
              Add Coupon
            </button>
          </div>
        </div>

        <div className="input-group mb-3">
          <input
            className="form-control"
            placeholder="Search coupon code..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button className="btn btn-outline-secondary">Search</button>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle" style={{ tableLayout: "fixed", width: "100%" }}>
            <thead className="table-light">
              <tr>
                <th>Sr.</th>
                <th>Coupon Code</th>
                <th>Status</th>
                <th>Type</th>
                <th>Discount</th>
                <th>User Usage</th>
                <th style={{ width: "170px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCoupons.length > 0 ? (
                filteredCoupons.map((coupon, index) => (
                  <tr key={coupon._id}>
                    <td>{(pageNo - 1) * limit + index + 1}</td>
                    <td className="fw-semibold">{coupon.code}</td>
                    <td>
                      <span
                        className={`badge ${coupon.isActive ? "bg-success" : "bg-secondary"}`}
                        role="button"
                        onClick={() => toggleCouponStatus(coupon)}
                      >
                        {coupon.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>{discountTypeLabel[coupon.discountType] || "-"}</td>
                    <td>{formatDiscount(coupon)}</td>
                    <td>{usageLabel[coupon.usagePerUser] || "-"}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <button className="btn btn-sm btn-outline-info" onClick={() => {
                          setSelectedCoupon(coupon);
                          setModalMode("view");
                        }}>
                          <CgEye size={16} />
                        </button>
                        <button className="btn btn-sm btn-outline-custom" onClick={() => openEdit(coupon)}>
                          <CgPen size={16} />
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => setDeleteId(coupon._id)}>
                          <CgTrash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center text-muted py-4">
                    No coupons found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-3">
          <button
            className="btn btn-outline-custom btn-sm"
            disabled={pageNo === 1}
            onClick={() => setPageNo((page) => Math.max(1, page - 1))}
          >
            Prev
          </button>
          <span>Page {pageNo} of {totalPages}</span>
          <button
            className="btn btn-outline-custom btn-sm"
            disabled={pageNo >= totalPages}
            onClick={() => setPageNo((page) => Math.min(page + 1, totalPages))}
          >
            Next
          </button>
        </div>
      </div>

      <Modal show={modalMode === "add" || modalMode === "edit"} onHide={() => setModalMode(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === "edit" ? "Edit Coupon" : "Add Coupon"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={saveCoupon}>
            <div className="mb-3">
              <label className="form-label">Coupon Code Name</label>
              <input
                className="form-control"
                value={form.code}
                placeholder="OFF5, OFFNEW10"
                onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={form.isActive ? "true" : "false"}
                onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.value === "true" }))}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">Type of Coupon</label>
              <select
                className="form-select"
                value={form.discountType}
                onChange={(e) => setForm((prev) => ({ ...prev, discountType: e.target.value }))}
              >
                <option value="PERCENTAGE">Flat Discount in Percentage</option>
                <option value="FIXED_AMOUNT">Flat Discount in Fixed Amount</option>
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">Discount Value</label>
              <input
                className="form-control"
                type="number"
                min="0"
                step="0.01"
                value={form.discountValue}
                placeholder={form.discountType === "PERCENTAGE" ? "5" : "100"}
                onChange={(e) => setForm((prev) => ({ ...prev, discountValue: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">How many times a single user can use it?</label>
              <select
                className="form-select"
                value={form.usagePerUser}
                onChange={(e) => setForm((prev) => ({ ...prev, usagePerUser: e.target.value }))}
              >
                <option value="ONCE">Once</option>
                <option value="MANY">Many</option>
              </select>
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setModalMode(null)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-outline-custom">
                Save
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <Modal show={modalMode === "view"} onHide={() => setModalMode(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Coupon Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCoupon && (
            <div className="row g-3">
              <div className="col-md-6">
                <div className="text-muted small">Coupon Code</div>
                <div className="fw-semibold">{selectedCoupon.code}</div>
              </div>
              <div className="col-md-6">
                <div className="text-muted small">Status</div>
                <span className={`badge ${selectedCoupon.isActive ? "bg-success" : "bg-secondary"}`}>
                  {selectedCoupon.isActive ? "Active" : "Inactive"}
                </span>
              </div>
              <div className="col-md-6">
                <div className="text-muted small">Type</div>
                <div>{discountTypeLabel[selectedCoupon.discountType]}</div>
              </div>
              <div className="col-md-6">
                <div className="text-muted small">Discount</div>
                <div>{formatDiscount(selectedCoupon)}</div>
              </div>
              <div className="col-md-6">
                <div className="text-muted small">User Usage</div>
                <div>{usageLabel[selectedCoupon.usagePerUser]}</div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <CustomModal
        show={Boolean(deleteId)}
        title="Confirm Delete"
        message="Are you sure you want to delete this coupon?"
        onConfirm={deleteCoupon}
        onCancel={() => setDeleteId(null)}
      />
    </>
  );
};

export default CouponsList;
